import { TrackEventMap } from '@core/TrackConstants'
import { useOtrack } from '@core/compositions/Otrack'
import { INSTANCE as FileUploadManager } from '@core/transmission/FileUploadManager'
import { INSTANCE as FileDownloadManager } from '@core/transmission/FileDownloadManager'
import { TaskState } from '@core/transmission/ITask'
import type { TaskManagerChangeListener } from './AbsFileTransmissionManager'
import { MicroAppId } from '@core/application/ApplicationConstants'
import { Action, NoticeType } from '@core/databus/IMessage'
import { generateDebounceNoticeSender } from '@/core/Notification/NotificationManager'
import type { AbsTask } from './AbsTask'

export const EXCEED_MAXIMUM_FILE_SIZE_ALARM_IS_PROMPTED = 'EXCEED_MAXIMUM_FILE_SIZE_ALARM_IS_PROMPTED'

export enum NoticeReadState {
  READING,
  UNREAD,
  READ,
}

export interface TaskCountInfo {
  uploadSuccess: number
  uploadError: number
  uploadTotal: number
  downloadSuccess: number
  downloadError: number
  downloadTotal: number
  pausedCount: number
  isRead: NoticeReadState
}

export enum TaskType {
  UPLOAD,
  DOWNLOAD,
}

export interface NoticeListener {
  onChange(taskCount: TaskCountInfo, state?: TaskState): void
}

class TransmissionNoticeManager {
  #isRead = NoticeReadState.UNREAD
  #taskCountInfo: TaskCountInfo = {
    uploadSuccess: 0,
    uploadError: 0,
    uploadTotal: 0,
    downloadSuccess: 0,
    downloadError: 0,
    downloadTotal: 0,
    pausedCount: 0,
    isRead: this.#isRead,
  }

  #noticeListener: NoticeListener | undefined = undefined

  constructor() {
    const overrunAlarmNotice = generateDebounceNoticeSender(
      {
        appId: MicroAppId.FILE_MANAGER,
        msg: 'owork_base.notice_message.notice_file_size_prompt',
        action: Action.CONTENT_PROMPT,
        type: NoticeType.COMMON_ACTION,
      },
      EXCEED_MAXIMUM_FILE_SIZE_ALARM_IS_PROMPTED
    )

    const { track } = useOtrack()

    const taskListener: TaskManagerChangeListener = {
      onAdd: (task: AbsTask) => this.#updateTransmissionNotice(task),
      onUpdated: (task: AbsTask) => this.#updateTransmissionNotice(task),
      onExceedMaximumFileSize: () => {
        track({ eventId: TrackEventMap.TRANSMISSION_OVERRUN_ALARM_NOTICE })
        overrunAlarmNotice()
      },
    }

    FileUploadManager.addTaskManagerChangeListener(taskListener)
    FileDownloadManager.addTaskManagerChangeListener(taskListener)
  }

  setNoticeListener(listener: NoticeListener | undefined): void {
    this.#noticeListener = listener
    const { isRead, ...taskCounts } = this.#taskCountInfo

    // 如果当前任务信息不为空，则立即触发一次信息变更提醒
    if (Object.values(taskCounts).some((taskCount) => taskCount !== 0)) {
      this.#noticeListener?.onChange(this.#taskCountInfo)
    }
  }

  set isRead(value: NoticeReadState) {
    this.#isRead = value
    if (this.#noticeListener) this.#noticeListener.onChange(this.#getTaskCount())
  }

  /**
   * 更新传输通知
   */
  #updateTransmissionNotice(task: AbsTask) {
    const taskCountInfo = this.#getTaskCount()

    if (!this.#isChanged(taskCountInfo)) return

    if (this.#isRead === NoticeReadState.READ) {
      this.isRead = NoticeReadState.UNREAD
    }
    this.#taskCountInfo = this.#getTaskCount()

    if (this.#noticeListener) this.#noticeListener.onChange(this.#taskCountInfo, task.state)
  }

  #getTaskCount(): TaskCountInfo {
    const uploadTasks = FileUploadManager.getAllTask()
    const downloadTasks = FileDownloadManager.getAllTask()

    let uploadSuccess = 0,
      uploadError = 0,
      downloadSuccess = 0,
      downloadError = 0,
      pausedCount = 0

    for (const task of uploadTasks) {
      if (task.state === TaskState.SUCCESS) uploadSuccess++
      if (task.state === TaskState.FAILED) uploadError++
      if (task.state === TaskState.PAUSE) pausedCount++
    }

    for (const task of downloadTasks) {
      if (task.state === TaskState.SUCCESS) downloadSuccess++
      if (task.state === TaskState.FAILED) downloadError++
      if (task.state === TaskState.PAUSE) pausedCount++
    }

    return {
      uploadSuccess,
      uploadError,
      uploadTotal: uploadTasks.length,
      downloadSuccess,
      downloadError,
      downloadTotal: downloadTasks.length,
      pausedCount,
      isRead: this.#isRead,
    }
  }

  #isChanged(newInfo: TaskCountInfo): boolean {
    return JSON.stringify(this.#taskCountInfo) !== JSON.stringify(newInfo)
  }
}

const INSTANCE = new TransmissionNoticeManager()

export { INSTANCE }
